import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import AlertsPanel from "../../components/marketInventory/AlertsPanel";
import {
  fetchLocationsSuccess,
  fetchLocationsFailure,
  fetchAlertsSuccess,
  fetchAlertsFailure,
  resolveAlertSuccess,
  resolveAlertFailure,
  checkExpiringSuccess,
  checkExpiringFailure
} from "../../store/marketInventory/actions";
import * as API from "../../store/marketInventory/api";
import { isStaffUser, isSuperUser } from "../../utils/AuthService";

const AlertsPanelContainer = ({
  alerts,
  locations,
  fetchLocationsSuccess,
  fetchLocationsFailure,
  fetchAlertsSuccess,
  fetchAlertsFailure,
  resolveAlertSuccess,
  resolveAlertFailure,
  checkExpiringSuccess,
  checkExpiringFailure
}) => {
  const isAdmin = isStaffUser() || isSuperUser();
  const [selectedLocationId, setSelectedLocationId] = useState(isAdmin ? null : "auto");

  // Fetch locations on mount (admin only - market users don't need to pick)
  useEffect(() => {
    if (!isAdmin) return;
    const loadLocations = async () => {
      try {
        const response = await API.fetchLocations();
        fetchLocationsSuccess(response.data);
        // Auto-select first location if available
        const locationsList = Array.isArray(response.data)
          ? response.data
          : (response.data.results || []);
        if (locationsList.length > 0) {
          setSelectedLocationId(locationsList[0].id);
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
        fetchLocationsFailure(error.response?.data?.message || "Failed to fetch locations");
      }
    };
    loadLocations();
  }, [isAdmin, fetchLocationsSuccess, fetchLocationsFailure]);

  const handleFetchAlerts = useCallback(async (locationId, alertType, activeOnly) => {
    try {
      const locId = isAdmin ? locationId : undefined;
      const response = await API.fetchAlerts(locId, alertType, activeOnly);
      fetchAlertsSuccess(response.data);
    } catch (error) {
      console.error("Error fetching alerts:", error);
      const errorMessage = error.response?.data?.message || "Failed to fetch alerts";
      fetchAlertsFailure(errorMessage);
    }
  }, [isAdmin, fetchAlertsSuccess, fetchAlertsFailure]);

  const handleResolveAlert = useCallback(async (alertId) => {
    try {
      const response = await API.resolveAlert(alertId);
      resolveAlertSuccess(response.data);
      return response.data;
    } catch (error) {
      console.error("Error resolving alert:", error);
      const errorMessage = error.response?.data?.message || "Failed to resolve alert";
      resolveAlertFailure(errorMessage);
      throw error;
    }
  }, [resolveAlertSuccess, resolveAlertFailure]);

  const handleCheckExpiring = useCallback(async (locationId, daysAhead) => {
    try {
      const locId = isAdmin ? locationId : undefined;
      const response = await API.checkExpiringItems(locId, daysAhead);
      checkExpiringSuccess(response.data);
      // Refresh alerts after checking
      await handleFetchAlerts(locationId, null, true);
      return response.data;
    } catch (error) {
      console.error("Error checking expiring items:", error);
      const errorMessage = error.response?.data?.message || "Failed to check expiring items";
      checkExpiringFailure(errorMessage);
      throw error;
    }
  }, [isAdmin, checkExpiringSuccess, checkExpiringFailure, handleFetchAlerts]);

  const handleLocationChange = (e) => {
    const locationId = e.target.value ? parseInt(e.target.value) : null;
    setSelectedLocationId(locationId);
  };

  // Get selected location name
  const selectedLocationName = Array.isArray(locations.list)
    ? locations.list.find(l => l.id === selectedLocationId)?.name
    : null;

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>
            <i className="fa fa-bell mr-05"></i>
            Inventory Alerts
          </span>
          {isAdmin && (
            <div className="d-flex align-items-center">
              <label className="mb-0 mr-05" style={{ whiteSpace: 'nowrap' }}>
                <i className="fa fa-map-marker mr-05"></i>
                Location:
              </label>
              <select
                className="form-control"
                style={{ width: '200px' }}
                value={selectedLocationId || ""}
                onChange={handleLocationChange}
                disabled={locations.loading}
              >
                <option value="">Select location...</option>
                {Array.isArray(locations.list) && locations.list.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {selectedLocationId ? (
        <AlertsPanel
          alerts={alerts}
          loading={alerts.loading}
          error={alerts.error}
          fetchAlerts={handleFetchAlerts}
          resolveAlert={handleResolveAlert}
          checkExpiring={handleCheckExpiring}
          locationId={selectedLocationId}
          locationName={selectedLocationName}
          isAdmin={isAdmin}
        />
      ) : (
        <div className="card">
          <div className="shipment-list--empty">
            <div className="fa fa-map-marker icon" aria-hidden="true"></div>
            <div>Please select a location to view alerts</div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  alerts: state.marketInventory.alerts,
  locations: state.marketInventory.locations
});

const mapDispatchToProps = {
  fetchLocationsSuccess,
  fetchLocationsFailure,
  fetchAlertsSuccess,
  fetchAlertsFailure,
  resolveAlertSuccess,
  resolveAlertFailure,
  checkExpiringSuccess,
  checkExpiringFailure
};

export default connect(mapStateToProps, mapDispatchToProps)(AlertsPanelContainer);
